import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { MovaraMark } from "@/components/Brand";
import { ArrowRight, ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const COMPANY_TYPES = [
  "Freight forwarder", "3PL / logistics operator", "Freight broker",
  "Courier / delivery team", "Warehouse + delivery operation", "Other",
];
const VOLUMES = [
  "Under 25 jobs/month", "25–100 jobs/month", "100–300 jobs/month", "300+ jobs/month", "Not sure",
];
const GOALS = [
  "Active shipment visibility", "Missing document tracking", "Customer updates", "Overdue next actions",
  "Closeout workflow", "Team handoffs", "Reporting", "Other",
];

const fieldCls = "h-10 w-full px-3 rounded-[5px] border border-line bg-surface text-sm text-ink placeholder:text-ink-3 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand";
const labelCls = "block text-[12.5px] font-medium text-ink mb-1.5";

function Header() {
  return (
    <header className="sticky top-0 z-30 bg-app-bg/90 backdrop-blur border-b border-line">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2.5 text-brand shrink-0">
          <MovaraMark className="h-6 w-7" />
          <span className="font-heading font-semibold text-[16px] sm:text-[17px] text-ink">MovaRaBoard</span>
        </Link>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <Link to="/sample-board" data-testid="req-sample-dashboard"
            className="h-9 px-3 sm:px-4 rounded-[5px] bg-brand text-white text-[13px] sm:text-[13.5px] font-medium flex items-center gap-1.5 hover:bg-brand-dark transition-colors whitespace-nowrap">
            <span className="hidden sm:inline">View Sample Board</span><span className="sm:hidden">Sample</span>
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </header>
  );
}

export default function RequestAccess() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", company_name: "", role_title: "",
    company_type: "", shipment_volume: "", improvement_goals: [], message: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleGoal = (g) =>
    setForm((f) => ({ ...f, improvement_goals: f.improvement_goals.includes(g) ? f.improvement_goals.filter((x) => x !== g) : [...f.improvement_goals, g] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) return toast.error("Please enter your full name.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return toast.error("Please enter a valid work email.");
    setLoading(true);
    try {
      await client.post("/access-requests", form);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-app-bg min-h-screen">
      <Header />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 md:py-14">
        <Link to="/" data-testid="req-back-home" className="text-[13px] text-ink-2 hover:text-brand flex items-center gap-1.5 mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>

        {submitted ? (
          <div data-testid="req-confirmation" className="bg-surface border border-line rounded-md p-8 sm:p-10 text-center">
            <div className="h-12 w-12 rounded-full bg-ok/10 border border-ok/30 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-6 w-6 text-ok" />
            </div>
            <h1 className="font-heading text-xl sm:text-2xl font-semibold text-ink">Thank you. Your request has been received.</h1>
            <p className="text-[14.5px] text-ink-2 mt-3 max-w-md mx-auto leading-relaxed">We'll follow up shortly with next steps.</p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/sample-board" data-testid="req-confirm-sample" className="h-10 px-5 rounded-[5px] bg-brand text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-brand-dark transition-colors">View Sample Board <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/" data-testid="req-confirm-home" className="h-10 px-5 rounded-[5px] border border-line bg-surface text-ink text-sm font-medium flex items-center justify-center hover:bg-surface-alt transition-colors">Back to home</Link>
            </div>
          </div>
        ) : (
          <>
            <p className="op-label text-steel mb-2">Get started</p>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-ink leading-tight">Request a Visibility Walkthrough</h1>
            <p className="text-[14.5px] text-ink-2 mt-3 leading-relaxed">
              Tell us a little about your logistics operation and we'll follow up with a walkthrough of MovaRaBoard.
            </p>

            <form onSubmit={submit} className="mt-8 bg-surface border border-line rounded-md p-5 sm:p-7 space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Full name <span className="text-danger">*</span></label>
                  <input data-testid="req-full-name" className={fieldCls} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Jordan Reyes" />
                </div>
                <div>
                  <label className={labelCls}>Work email <span className="text-danger">*</span></label>
                  <input data-testid="req-email" type="email" className={fieldCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jordan@yourcompany.com" />
                </div>
                <div>
                  <label className={labelCls}>Company name</label>
                  <input data-testid="req-company" className={fieldCls} value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="Acme Logistics" />
                </div>
                <div>
                  <label className={labelCls}>Role / title</label>
                  <input data-testid="req-role" className={fieldCls} value={form.role_title} onChange={(e) => set("role_title", e.target.value)} placeholder="Operations Manager" />
                </div>
                <div>
                  <label className={labelCls}>Company type</label>
                  <select data-testid="req-company-type" className={fieldCls} value={form.company_type} onChange={(e) => set("company_type", e.target.value)}>
                    <option value="">Select…</option>
                    {COMPANY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Approx. shipment / job volume</label>
                  <select data-testid="req-volume" className={fieldCls} value={form.shipment_volume} onChange={(e) => set("shipment_volume", e.target.value)}>
                    <option value="">Select…</option>
                    {VOLUMES.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className={labelCls}>What are you trying to improve?</label>
                <div className="grid sm:grid-cols-2 gap-2 mt-1">
                  {GOALS.map((g) => {
                    const checked = form.improvement_goals.includes(g);
                    return (
                      <button key={g} type="button" data-testid={`req-goal-${g.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => toggleGoal(g)}
                        className={`flex items-center gap-2.5 px-3 h-10 rounded-[5px] border text-[13px] text-left transition-colors ${checked ? "border-brand bg-brand/[0.06] text-ink" : "border-line bg-surface text-ink-2 hover:border-steel"}`}>
                        <span className={`h-4 w-4 rounded-[3px] border flex items-center justify-center shrink-0 ${checked ? "bg-brand border-brand" : "border-line"}`}>
                          {checked && <CheckCircle2 className="h-3 w-3 text-white" />}
                        </span>
                        {g}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className={labelCls}>Message / notes</label>
                <textarea data-testid="req-message" rows={4} value={form.message} onChange={(e) => set("message", e.target.value)}
                  className="w-full px-3 py-2.5 rounded-[5px] border border-line bg-surface text-sm text-ink placeholder:text-ink-3 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand resize-none"
                  placeholder="Share anything helpful about how your team currently tracks shipment jobs." />
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-1">
                <button type="submit" data-testid="req-submit" disabled={loading}
                  className="h-11 px-6 rounded-[5px] bg-brand text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-brand-dark transition-colors disabled:opacity-60">
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : <>Submit Request <ArrowRight className="h-4 w-4" /></>}
                </button>
                <Link to="/sample-board" data-testid="req-form-sample"
                  className="h-11 px-6 rounded-[5px] border border-line bg-surface text-ink text-sm font-medium flex items-center justify-center hover:bg-surface-alt transition-colors">
                  View Sample Board
                </Link>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
